import { getSupabase } from '../lib/supabase';
import { isAuthConfigured } from '../lib/runtimeConfig';
import { portalFederatedSession } from '../lib/federatedSession';

// Portal sign-in = GoTrue, exactly like the staff app (web/CLAUDE.md rule 2: supabase-js stays
// behind lib/auth.tsx + lib/supabase.ts). The portal only needs three things from it: the current
// access token for the Bearer header, password sign-in, and sign-out. Which tenant / contact the
// token maps to is the API's business (portal_user.auth_user_id → claims → RLS), never the SPA's.

export const portalAuthConfigured = isAuthConfigured;

// A contact of an organisation that runs on its HaloPSA signs in with their Halo login instead: the
// API holds the session (src/lib/federatedSession.ts, the portal's own cookie) and the portal is
// served from Halo as that contact. Whichever sign-in is active supplies the bearer.

/** Bearer for the portal client; null when signed out or when no identity provider is configured. */
export async function portalToken(): Promise<string | null> {
  if (portalFederatedSession.get() || portalFederatedSession.wasActive()) {
    const t = await portalFederatedSession.token();
    if (t) return t;
  }
  if (!isAuthConfigured) return null;
  try {
    const { data } = await getSupabase().auth.getSession();
    return data.session?.access_token ?? null;
  } catch {
    return null;
  }
}

export async function portalSignIn(email: string, password: string): Promise<void> {
  if (!isAuthConfigured) throw new Error('Sign-in is not configured on this installation. Ask your provider for a portal invitation.');
  const { error } = await getSupabase().auth.signInWithPassword({ email, password });
  if (error) throw error;
}

export async function portalResetPassword(email: string): Promise<void> {
  if (!isAuthConfigured) throw new Error('Sign-in is not configured on this installation.');
  const { error } = await getSupabase().auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/reset` });
  if (error) throw error;
}

export async function portalSignOut(): Promise<void> {
  if (portalFederatedSession.get() || portalFederatedSession.wasActive()) await portalFederatedSession.signOut().catch(() => undefined);
  if (!isAuthConfigured) return;
  try {
    await getSupabase().auth.signOut();
  } catch {
    /* already signed out */
  }
}

/** This portal's organisation, if it signs its customers in with Halo (by the portal's host). */
export function portalHaloRoute() {
  return portalFederatedSession.routeForHost();
}

/** Start the Halo sign-in for the organisation. */
export function portalHaloBegin(tenantId: string): Promise<void> {
  return portalFederatedSession.begin(tenantId);
}

/** Back from Halo: finish the sign-in. */
export function portalHaloComplete(code: string, state: string) {
  return portalFederatedSession.complete(code, state);
}
